import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

import { getBaseUrl } from "../config";
import { getAuthApiHeaders } from "../../utils/common";
import { showSnackbar } from "./snackbar";
import { getApiErrorMessage } from "../../utils/apiError";

const baseUrl = getBaseUrl();

/**
 * Pull the download file name out of a Content-Disposition header.
 *
 * Handles both the RFC 5987 extended form (`filename*=UTF-8''QBR%20Q3.docx`)
 * and the plain form (`filename="QBR_Q32026.docx"`). The extended form wins
 * when both are present, matching browser behaviour. Returns `null` when the
 * header is absent or carries no usable name, so the caller can fall back.
 */
export function parseFilename(contentDisposition) {
  if (!contentDisposition) return null;

  const extended = contentDisposition.match(
    /filename\*\s*=\s*(?:UTF-8|utf-8)''([^;]+)/i,
  );
  if (extended?.[1]) {
    try {
      return decodeURIComponent(extended[1].trim());
    } catch {
      // Malformed percent-encoding — fall through to the plain form.
    }
  }

  const quoted = contentDisposition.match(/filename\s*=\s*"([^"]+)"/i);
  if (quoted?.[1]) return quoted[1].trim();

  const bare = contentDisposition.match(/filename\s*=\s*([^;]+)/i);
  if (bare?.[1]) return bare[1].trim();

  return null;
}

/** Sensible name when the server sends no usable Content-Disposition. */
function defaultFilename(quarter) {
  const q = (quarter || "").toString().trim();
  return q ? `QBR_Report_${q}.docx` : "QBR_Report.docx";
}

/**
 * Download the QBR report document from `GET /kpi_metrics/qbr_report/export`.
 *
 * Reuses the same axios + auth header pattern as the other KPI/QBR calls
 * (`getAuthApiHeaders`, `use-case`, `client`), asking for a blob so the
 * browser receives the raw `.docx`. `quarter` and `productGroup` map to the
 * endpoint's optional query params; omit `quarter` for the current (live)
 * quarter. Triggers the browser download and resolves with the file name;
 * failures surface through the shared snackbar.
 */
export const exportQbrReportApi = createAsyncThunk(
  "qbr-export",
  async ({ quarter, productGroup } = {}, { rejectWithValue, dispatch }) => {
    try {
      const params = {};
      if (quarter) params.quarter = quarter;
      if (productGroup) params.product_group = productGroup;

      const response = await axios.get(
        `${baseUrl}/kpi_metrics/qbr_report/export`,
        {
          headers: {
            "use-case": "src",
            ...getAuthApiHeaders(),
            client: "UT",
          },
          params,
          responseType: "blob",
          withCredentials: true,
        },
      );

      const filename =
        parseFilename(response.headers?.["content-disposition"]) ||
        defaultFilename(quarter);

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      return filename;
    } catch (error) {
      dispatch(
        showSnackbar({
          message: `Could not export the QBR report: ${getApiErrorMessage(
            error,
          )}`,
          severity: "error",
        }),
      );
      return rejectWithValue(getApiErrorMessage(error));
    }
  },
  {
    // One download at a time — ignore a second click while one is in flight.
    condition: (_arg, { getState }) => !getState()?.qbrExport?.isLoading,
  },
);

const qbrExportSlice = createSlice({
  name: "qbr-export",
  initialState: {
    isLoading: false,
    isError: false,
    error: null,
  },
  extraReducers: (builder) => {
    builder.addCase(exportQbrReportApi.pending, (state) => {
      state.isLoading = true;
      state.isError = false;
      state.error = null;
    });
    builder.addCase(exportQbrReportApi.fulfilled, (state) => {
      state.isLoading = false;
    });
    builder.addCase(exportQbrReportApi.rejected, (state, action) => {
      state.isLoading = false;
      state.isError = true;
      state.error = action.payload;
    });
  },
});

export default qbrExportSlice.reducer;
